import { useAppDispatch } from 'app/hooks';
import { useEffect } from 'react';
import type { History } from 'history';
import { useHistory, useLocation } from 'react-router-dom';
import appService from 'services/appService';
import {
  CANVA_SSO_EXCHANGE_STORAGE_PREFIX,
  getCanvaExchangeCode,
} from 'utils/canvaSso';

const STORAGE_PREFIX = CANVA_SSO_EXCHANGE_STORAGE_PREFIX;

function stripCanvaExchangeFromUrl(
  history: History,
  pathname: string,
  search: string
) {
  const params = new URLSearchParams(search);
  params.delete('canvaExchange');
  const next = params.toString();
  history.replace({
    pathname,
    search: next ? `?${next}` : '',
  });
}

/**
 * Runs on every route: if the URL contains {@code canvaExchange}, exchanges it for a RevShop JWT,
 * hydrates the session, then removes the query param (see gateway {@code GET /sso/canva/consume}).
 */
const CanvaSsoExchangeBootstrap = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const code = getCanvaExchangeCode(location.search);
    if (!code) {
      return;
    }

    const lockKey = `${STORAGE_PREFIX}${code}`;
    if (sessionStorage.getItem(lockKey) === 'ok') {
      stripCanvaExchangeFromUrl(history, location.pathname, location.search);
      return;
    }

    // Prevent App mount from hydrating a stale session while Canva SSO is in flight.
    sessionStorage.setItem(lockKey, 'pending');

    let cancelled = false;

    (async () => {
      dispatch.app.showLoading();
      try {
        await dispatch.app.completeCanvaSsoExchange(code);
        if (cancelled) {
          return;
        }
        sessionStorage.setItem(lockKey, 'ok');
        stripCanvaExchangeFromUrl(history, location.pathname, location.search);
      } catch {
        sessionStorage.removeItem(lockKey);
        if (cancelled) {
          return;
        }
        if (appService.getUserFromJwtToken()) {
          sessionStorage.setItem(lockKey, 'ok');
          stripCanvaExchangeFromUrl(history, location.pathname, location.search);
          return;
        }
        history.replace({
          pathname: '/login',
          search: '?canva_sso_error=exchange_failed',
        });
      } finally {
        if (!cancelled) {
          dispatch.app.hideLoading();
        }
      }
    })();

    return () => {
      cancelled = true;
      if (sessionStorage.getItem(lockKey) === 'pending') {
        sessionStorage.removeItem(lockKey);
      }
    };
  }, [location.pathname, location.search, history, dispatch]);

  return null;
};

export default CanvaSsoExchangeBootstrap;
